import React from "react"
import useStatus from "./useStatus"
import { Box, Button, Chip, Grid, Typography } from "@mui/material"
import { formatDate, formatNumber, statusNotifications } from "../../../helpers/UtilityHelper"
import DashboardSkeleton from "../DashboardSkeleton"
import ProzoDataGrid from "../../../common/ProzoDataGrid"
import { FormProvider, RHFDateField, RHFSelectField, RHFTextField } from "../../../common/hook-form"
import _ from "lodash"
import { useForm } from "react-hook-form"
import WhatsAppChatPopup from "./Conversation"
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography'
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR } from '../../../styles/foundation/colors'
import { dataGridWrapSx } from '../../../styles/patterns'

// ── KPI card (inline, replaces oversized StatCard with phone images) ──
const kpiCardSx = (accent) => ({
    bgcolor: WHITE, border: `1px solid ${BORDER}`, borderLeft: `4px solid ${accent}`,
    borderRadius: '10px', p: 2, display: 'flex', alignItems: 'center', gap: 1.5, height: '100%',
})
const kpiIconSx = (accent) => ({
    width: 40, height: 40, borderRadius: '10px', bgcolor: `${accent}12`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: accent, flexShrink: 0, '& svg': { fontSize: 22 },
})

let chartColumns = [
    { label: 'Order Placed', dataKey: 'proship_order_placed' },
    { label: 'In Transit', dataKey: 'proship_order_in_transit' },
    { label: 'Out For Delivery', dataKey: 'proship_out_for_delivery' },
    { label: 'Picked Up', dataKey: 'proship_order_picked_up' },
    { label: 'Not Delivered', dataKey: 'proship_order_not_delivered' },
    { label: 'Delivered', dataKey: 'proship_order_delivered' },
    { label: 'Delivery Delay', dataKey: 'proship_delivery_delay' },
]

export default function Status() {
    // Filters + paging live here so they can drive the server-side query.
    const method = useForm()
    const { watch } = method
    const templateName = watch('template')
    const mobileNumber = watch('mobile')
    const awbNumber = watch('awbNumber')
    const createdDate = watch('createdDate')

    const [page, setPage] = React.useState(0)
    const [sizePerPage, setSizePerPage] = React.useState(50)
    // Any filter change (or page-size change) must jump back to the first page,
    // otherwise the offset can point past the end of the filtered result set.
    React.useEffect(() => { setPage(0) }, [templateName, mobileNumber, awbNumber, createdDate, sizePerPage])

    const filters = { templateName, mobileNumber, awbNumber, createdDate }
    const { isLoading, data, totalSent } = useStatus({ page, sizePerPage, filters })
    const [conversation, setConversation] = React.useState(null)

    const sent = totalSent || 0

    // "Messages Sent" comes straight from the server count and stays accurate.
    // Read% / Undelivered are whole-range breakdowns that need a backend
    // aggregation endpoint (see docs/NEW_APIS_NEEDED.md) — shown as pending.
    const kpis = [
        { label: 'Messages Sent', value: formatNumber(sent), icon: <SendOutlinedIcon />, accent: PRIMARY_BLUE },
        { label: 'Read %', value: '—', icon: <MarkEmailReadOutlinedIcon />, accent: SUCCESS, pending: true },
        { label: 'Undelivered', value: '—', icon: <ErrorOutlineIcon />, accent: ERROR, pending: true },
    ]

    if (isLoading) return <DashboardSkeleton isDashboard />

    const tableData = data && data.length > 0 ? data : []

    return (
        <>
            {/* KPI cards */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                {kpis.map((k) => (
                    <Grid item xs={12} sm={4} key={k.label}>
                        <Box sx={kpiCardSx(k.accent)}>
                            <Box sx={kpiIconSx(k.accent)}>{k.icon}</Box>
                            <Box>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    {k.label}
                                </Typography>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xl'], fontWeight: WEIGHT.extra, color: NAVY, lineHeight: 1.2 }}>
                                    {k.value}
                                </Typography>
                                {k.pending && (
                                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.2 }}>
                                        Pending backend support
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Chart — whole-range breakdown by date needs a backend aggregation endpoint */}
            <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', p: 2.5, mb: 2.5 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY, mb: 1.5 }}>
                    Message Volume by Status
                </Typography>
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>Aggregated view pending</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5 }}>
                        The by-date breakdown needs a backend aggregation endpoint and will return once that is available.
                    </Typography>
                </Box>
            </Box>

            {/* Table — server-side paginated message log */}
            <TabularData
                data={tableData}
                rowCount={sent}
                method={method}
                page={page} setPage={setPage}
                sizePerPage={sizePerPage} setSizePerPage={setSizePerPage}
                setConversation={setConversation}
            />

            {conversation && <WhatsAppChatPopup conversation={conversation} setConversation={setConversation} />}
        </>
    )
}

function createMessageText(payload, template, template_name) {
    if (!payload || !Array.isArray(payload)) return '';
    if (payload.length === 0) return '';

    let k = ''
    switch (template_name) {
        case 'proship_order_delivered':
        case 'proship_out_for_delivery':
        case 'proship_order_not_delivered':
        case 'proship_delivery_delay':
            k = template?.replace("{customer_name}", payload[0]?.text)
                .replace("{brand_name}", payload[1]?.text)
                .replace("{order_id}", payload[2]?.text)
                .replace("{tracking_url}", payload[3]?.text + "/" + payload[4]?.text)
            break;
        case 'proship_order_in_transit':
            k = template?.replace("{customer_name}", payload[0]?.text)
                .replace("{brand_name}", payload[1]?.text)
                .replace("{order_id}", payload[2]?.text)
                .replace("{courier_name}", payload[3]?.text)
                .replace("{ed_date}", payload[4]?.text)
                .replace("{tracking_url}", payload[5]?.text + "/" + payload[6]?.text)
            break;
        case 'proship_order_picked_up':
            k = template?.replace("{customer_name}", payload[0]?.text)
                .replace("{brand_name}", payload[1]?.text)
                .replace("{order_id}", payload[2]?.text)
                .replace("{courier_name}", payload[3]?.text)
                .replace("{tracking_url}", payload[4]?.text + "/" + payload[5]?.text)
            break;
        case 'proship_order_placed':
            k = template?.replace("{customer_name}", payload[0]?.text)
                .replace("{order_id}", payload[1]?.text)
                .replace("{product_name}", payload[2]?.text)
                .replace("{brand_name}", payload[3]?.text)
                .replace("{tracking_url}", payload[4]?.text + "/" + payload[5]?.text)
            break;
        default: k = '';
    }
    return k;
}

function TabularData({ data, rowCount, method, page, setPage, sizePerPage, setSizePerPage, setConversation }) {
    const notifications = statusNotifications;
    const finalData = [...data].map(row => {
        let k = '';
        switch (row.template_name) {
            case 'proship_order_placed': k = 'PLACED'; break;
            case 'proship_order_in_transit': k = 'LIVE'; break;
            case 'proship_out_for_delivery': k = 'OUT_FOR_DELIVERY'; break;
            case 'proship_order_picked_up': k = 'PICKED'; break;
            case 'proship_order_not_delivered': k = 'NOT_DELIVERED'; break;
            case 'proship_order_delivered': k = 'DELIVERED'; break;
            case 'proship_delivery_delay': k = 'DELAY'; break;
            case 'proship_delivery_failed': k = 'NDR'; break;
            default: k = 'UNKNOWN';
        }
        const defaultText = _.find(notifications, { val: k })?.msg
        let convData = [];
        if (row?.payload_body?.template?.bodyValues) {
            convData = row.payload_body.template.bodyValues.map(text => ({ text }));
        } else if (Array.isArray(row?.payload_body?.template?.components)) {
            convData = row.payload_body.template.components[0]?.parameters || [];
        }
        const bodyResult = createMessageText(convData, defaultText, row.template_name);
        return { ...row, conversation_history: [{ action: 'message', timestamp: row.created_date, body: bodyResult }] };
    });

    const columns = [
        { field: "identifier", headerName: "Recipient", flex: 1, renderCell: ({ row }) => { const c = row?.payload_body?.to || ""; return <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>*******{c.slice(-3)}</Typography> } },
        { field: "awb", headerName: "AWB", flex: 1, renderCell: ({ row }) => <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>{row?.meta_data?.awbNumber || ''}</Typography> },
        { field: "template_name", headerName: "Status", flex: 1, renderCell: ({ row }) => { const t = _.find(chartColumns, { dataKey: row.template_name }); return <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>{t ? t.label : ''}</Typography> } },
        { field: "status", headerName: "Message Status", flex: 0.8, renderCell: ({ row }) => row.status ? <Chip label={row.current_status?.toUpperCase()} size="small" sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold }} /> : "—" },
        { field: "created_at", headerName: "Date", flex: 1, renderCell: ({ row }) => <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>{formatDate(row.created_date)}</Typography> },
        { field: "conversationsSummary", headerName: "Actions", flex: 0.8, renderCell: ({ row }) => <Button size="small" onClick={() => setConversation(row)} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE }}>View</Button> },
    ];

    return (
        <Box sx={dataGridWrapSx}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}` }}>
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>Message Log</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
                <FormProvider methods={method}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={3}>
                            <RHFSelectField size='small' width="100%" label="Shipment Status" name="template" options={[{ label: 'All', value: undefined }, ...chartColumns.map((r) => ({ label: r.label, value: r.dataKey }))]} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <RHFTextField label="AWB Number" name="awbNumber" size='small' />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <RHFTextField label="Mobile Number" name="mobile" size='small' />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <RHFDateField label="Created Date" name="createdDate" size='small' />
                        </Grid>
                    </Grid>
                </FormProvider>
                <ProzoDataGrid
                    columns={columns}
                    rows={finalData}
                    page={page} setPage={setPage}
                    sizePerPage={sizePerPage} setSizePerPage={setSizePerPage}
                    hideFooterPagination={false} rowCount={rowCount}
                    getRowId={(row) => row.identifier + row._id.timestamp}
                    autoHeight={true} rowHeight={50} pagination={true} paginationMode={"server"}
                />
            </Box>
        </Box>
    )
}
